// Scoped reads for post-mutation invoice reconciles. Split out of
// invoiceService.js (500-line cap) and re-exported there — same pattern as
// invoiceVoidOps.js / invoiceYears.js / estimateInvoiceLink.js. Pure move,
// no behavior change.
//
// getInvoices(tenantId) with no `year` is an all-time, all-status read of
// every invoice the tenant ever had — correct (fetchAllRows pages past the
// 1000-row cap) but expensive, and ~12 post-mutation reconciles across admin
// + billing called it just to patch ONE row + resync the drafts chip. These
// two reads replace that: fetch exactly the touched invoice(s), plus the
// tenant's current (bounded) draft set, and let
// reconcileInvoicesAfterMutation.js merge them onto existing state.
import { supabase } from '../supabase';
import { fetchAllRows } from '../utils/async/fetchAllRows';
import { getInvoiceSnapshot } from '../utils/invoice/invoiceSnapshot';

// Same select shape as getInvoices — the customers embed + reminders_paused_at
// every list/reconcile read needs.
// ONE invoice list select shape — getInvoices (invoiceService.js) imports it,
// so the post-mutation reconcile always returns rows shaped like the list.
export const INVOICE_LIST_COLS = `*, reminders_paused_at, customers (full_name, email, cc_emails, comm_opt_out, service_address)`;

// PostgREST URL length: an `.in('id', [...])` filter with too many UUIDs
// (36 chars each) can exceed the request-line limit. 150 ids keeps every
// chunk comfortably under that with room for the rest of the query string.
const INVOICE_ID_CHUNK_SIZE = 150;

/**
 * Fetch exactly the invoices named by `ids` (any tenant year, any status) —
 * the scoped read a post-mutation reconcile uses instead of an all-time
 * `getInvoices(tenantId)` (data-freshness doctrine; see
 * reconcileInvoicesAfterMutation.js). Soft-deleted rows are excluded, so an
 * id whose invoice was deleted/voided-away simply comes back missing — the
 * caller's merge treats that as "gone".
 *
 * Chunked at `INVOICE_ID_CHUNK_SIZE` ids per request (URL length) and each
 * chunk goes through `fetchAllRows` so a chunk can never silently truncate
 * at PostgREST's 1000-row cap. Any chunk error fails the WHOLE read.
 *
 * @param {string|null} tenantId
 * @param {Array<string>} ids
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getInvoicesByIds = async (tenantId = null, ids = []) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const cleanIds = [...new Set((ids || []).filter(Boolean))];
  if (cleanIds.length === 0) return { data: [], error: null };

  const chunks = [];
  for (let i = 0; i < cleanIds.length; i += INVOICE_ID_CHUNK_SIZE) {
    chunks.push(cleanIds.slice(i, i + INVOICE_ID_CHUNK_SIZE));
  }

  const results = await Promise.all(chunks.map((chunk) => fetchAllRows((opts) =>
    supabase
      .from('invoices')
      .select(INVOICE_LIST_COLS, opts)
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)
      .in('id', chunk)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true }),
  )));

  const failed = results.find((r) => r.error);
  if (failed) return { data: null, error: failed.error };
  return { data: results.flatMap((r) => r.data || []), error: null };
};

/**
 * Fetch every LIVE draft invoice for a tenant (no year scope — the drafts set
 * is bounded by definition, unlike "every invoice ever"). Used by
 * reconcileInvoicesAfterMutation.js to keep the Drafts/Por-facturar chips
 * honest after any single-invoice mutation, without an all-time
 * `getInvoices(tenantId)` read.
 *
 * @param {string|null} tenantId
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getDraftInvoices = async (tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const res = await fetchAllRows((opts) =>
    supabase
      .from('invoices')
      .select(INVOICE_LIST_COLS, opts)
      .eq('tenant_id', tenantId)
      .eq('status', 'draft')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true }),
  );
  return { data: res.data, error: res.error };
};

/**
 * The customer's open lifecycle draft(s) (invoice_origin 'lifecycle' lives in the snapshot), reduced to
 * what the activate-from-review retry needs: the invoice id, its visit ids and its total (KAN-1031).
 *
 * @returns {Promise<{data: {invoiceId: string, jobIds: string[], total: number|null}|null, error: Error|null}>}
 */
export const getOpenLifecycleDraftForCustomer = async (tenantId = null, customerId = null) => {
  if (!tenantId || !customerId) return { data: null, error: new Error('tenantId and customerId are required') };
  const { data, error } = await supabase
    .from('invoices')
    .select('id, total, snapshot')
    .eq('tenant_id', tenantId)
    .eq('customer_id', customerId)
    .eq('status', 'draft')
    .eq('snapshot->>invoice_origin', 'lifecycle')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1); // bounded: the newest open lifecycle draft is the one just staged
  if (error) return { data: null, error };
  const inv = (data || [])[0];
  if (!inv) return { data: null, error: null };
  const snap = getInvoiceSnapshot(inv);
  const jobIds = (Array.isArray(snap?.jobs) ? snap.jobs : []).map((j) => j?.id).filter(Boolean).map(String);
  const total = inv.total == null ? null : Number(inv.total);
  return { data: { invoiceId: String(inv.id), jobIds, total }, error: null };
};
